import { Controller } from '@hotwired/stimulus';
import L, { Map as LeafletMap, TileLayer } from 'leaflet';
import 'leaflet/dist/leaflet.css';

const mapDataCache = new Map();
const mapDataRequestCache = new Map();
const DEFAULT_MAP_CENTER = [-13.5, 34.5];
const DEFAULT_MAP_ZOOM = 7;
const POPUP_WIDTH_PX = 160;

export default class extends Controller {
  static targets = ['map', 'error', 'stats', 'loading'];
  static values = {
    dataUrl: String,
    sampledSalesLabel: { type: String },
    sampledClustersLabel: { type: String },
    unknownClusterLabel: { type: String },
    clusterPopupTitleLabel: { type: String },
    clusterPopupSampledSalesLabel: { type: String },
    claimPopupPrefixLabel: { type: String },
    claimPopupClusterPrefixLabel: { type: String },
    serialNumberLabel: { type: String },
    mapRenderErrorLabel: { type: String },
    mapDataRequestFailedLabel: { type: String },
  };

  async connect() {
    if (!this.hasMapTarget || !this.hasDataUrlValue) return;

    this.disconnected = false;

    // Reserve layout space but hide incomplete map layers until everything is ready.
    this.mapTarget.classList.add('invisible');

    this.defaultView = { center: DEFAULT_MAP_CENTER, zoom: DEFAULT_MAP_ZOOM };
    this.map = new LeafletMap(this.mapTarget, { worldCopyJump: true }).setView(
      this.defaultView.center,
      this.defaultView.zoom
    );
    new TileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      minZoom: 5,
      noWrap: true,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(this.map);

    try {
      const data = await this.fetchMapData(this.dataUrlValue);
      if (this.disconnected || !this.map) return;
      this.defaultView = this.defaultViewFromMeta(data?.meta);
      this.map.setView(this.defaultView.center, this.defaultView.zoom);
      this.updateStats(data?.meta);
      const clusterLayer = new L.GeoJSON(data.clusters, {
        style: { color: '#0f766e', weight: 2, opacity: 0.95, fillOpacity: 0.18 },
        onEachFeature: (feature, layer) => {
          const clusterId = feature?.properties?.cluster_id || this.unknownClusterLabelValue;
          const count = feature?.properties?.sampled_sales_count || 0;
          layer.bindPopup(
            `${this.clusterPopupTitleLabelValue} ${clusterId}<br/>${this.clusterPopupSampledSalesLabelValue}: ${count}`,
            {
              minWidth: POPUP_WIDTH_PX,
              maxWidth: POPUP_WIDTH_PX,
            }
          );
        },
      });
      clusterLayer.addTo(this.map);

      const pointOffsets = {};
      const pointsLayer = new L.GeoJSON(data.points, {
        pointToLayer: (feature, latlng) => {
          const key = `${latlng.lat.toFixed(6)},${latlng.lng.toFixed(6)}`;
          const n = pointOffsets[key] || 0;
          pointOffsets[key] = n + 1;

          // Spread duplicate coordinates slightly in a small ring so overlaps are visible.
          const angle = n * 2.399963229728653; // golden angle
          const r = 0.00035 * Math.sqrt(n); // ~40m scale jitter
          const jitteredLatLng =
            n === 0 ? latlng : [latlng.lat + Math.sin(angle) * r, latlng.lng + Math.cos(angle) * r];

          return new L.CircleMarker(jitteredLatLng, {
            radius: 2.5,
            color: 'transparent',
            fillColor: '#dc2626',
            weight: 0,
            fillOpacity: 0.9,
          });
        },
        onEachFeature: (feature, layer) => {
          const claimId = feature?.properties?.claim_id;
          const clusterId = feature?.properties?.cluster_id;
          const serialNumber = feature?.properties?.device_serial_number;
          const popupTitle = `${this.claimPopupPrefixLabelValue}${claimId}${clusterId ? ` - ${this.claimPopupClusterPrefixLabelValue} ${clusterId}` : ''}`;
          const serialLine = serialNumber
            ? `<br/>${this.serialNumberLabelValue}: ${serialNumber}`
            : '';
          layer.bindPopup(`${popupTitle}${serialLine}`, {
            minWidth: POPUP_WIDTH_PX,
            maxWidth: POPUP_WIDTH_PX,
          });
        },
      }).addTo(this.map);

      // Ensure Leaflet recalculates tile layout after the element is painted.
      requestAnimationFrame(() => this.map.invalidateSize(true));

      // Prefer sampled cluster bounds for map framing.
      const clusterBounds = clusterLayer.getBounds();
      if (clusterBounds && clusterBounds.isValid()) {
        this.map.fitBounds(clusterBounds, { padding: [20, 20], maxZoom: 9 });
      } else {
        const pointBounds = pointsLayer.getBounds();
        if (pointBounds && pointBounds.isValid()) {
          this.map.fitBounds(pointBounds, { padding: [20, 20], maxZoom: 11 });
        } else {
          this.map.setView(this.defaultView.center, this.defaultView.zoom);
        }
      }

      if (this.disconnected || !this.map) return;
      if (this.hasLoadingTarget) this.loadingTarget.classList.add('hidden');
      this.mapTarget.classList.remove('invisible');
    } catch (error) {
      if (this.disconnected) return;
      this.showError(error?.message || this.mapRenderErrorLabelValue);
      if (this.hasLoadingTarget) this.loadingTarget.classList.add('hidden');
      this.mapTarget.classList.remove('invisible');
    }
  }

  disconnect() {
    this.disconnected = true;
    if (this.map) this.map.remove();
    this.map = null;
  }

  async fetchMapData(url) {
    if (mapDataCache.has(url)) return mapDataCache.get(url);
    if (mapDataRequestCache.has(url)) return mapDataRequestCache.get(url);

    const request = fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok)
          throw new Error(`${this.mapDataRequestFailedLabelValue} ${response.status}`);
        return response.json();
      })
      .then((data) => {
        mapDataCache.set(url, data);
        return data;
      })
      .finally(() => {
        mapDataRequestCache.delete(url);
      });

    mapDataRequestCache.set(url, request);
    return request;
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove('hidden');
  }

  updateStats(meta) {
    if (!this.hasStatsTarget || !meta) return;
    const sampledSales = meta.points_count ?? 0;
    const sampledClusters = meta.sampled_cluster_count ?? 0;
    this.statsTarget.textContent = `${this.sampledSalesLabelValue}: ${sampledSales} | ${this.sampledClustersLabelValue}: ${sampledClusters}`;
  }

  defaultViewFromMeta(meta) {
    const center = meta?.default_view?.center;
    const zoom = meta?.default_view?.zoom;
    if (!Array.isArray(center) || center.length < 2) {
      return { center: DEFAULT_MAP_CENTER, zoom: DEFAULT_MAP_ZOOM };
    }

    return {
      center: [Number(center[0]), Number(center[1])],
      zoom: Number.isFinite(Number(zoom)) ? Number(zoom) : DEFAULT_MAP_ZOOM,
    };
  }
}
